import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, LogIn, LogOut, User } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { logoutUser } from '../store/slices/authSlice';

const NavLink = React.memo(({ to, children, isActive }) => (
  <Link
    to={to}
    className={`relative px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
      isActive ? 'text-white' : 'text-gray-300 hover:text-white'
    }`}
  >
    {children}
    {isActive && (
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500"
        layoutId="underline"
        initial={false}
      />
    )}
  </Link>
));

const MobileNavLink = React.memo(({ to, children, onClick, isActive }) => (
  <Link
    to={to}
    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-300 ${
      isActive ? 'bg-indigo-700 text-white' : 'text-gray-300 hover:bg-indigo-700 hover:text-white'
    }`}
    onClick={onClick}
  >
    {children}
  </Link>
));

const DropdownLink = React.memo(({ to, children }) => (
  <Link
    to={to}
    className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-900 transition-all duration-300"
  >
    {children}
  </Link>
));

const LoginButton = React.memo(({ onClick }) => (
  <div className="flex items-center space-x-4">
    <Link
      to="/login"
      className="flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
    >
      <LogIn className="w-4 h-4 mr-2" />
      Login
    </Link>
    <Link
      to="/register"
      className="flex items-center px-4 py-2 rounded-md text-sm font-medium border border-indigo-400 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
    >
      <User className="w-4 h-4 mr-2" />
      Register
    </Link>
  </div>
));

const getUserRole = (role) => {
  if (role === 'system_admin') return 'admin';
  return (role === 'customer' || role === 'customer_admin') ? 'customer' : 'carrier';
};

const UserMenu = React.memo(({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300"
      >
        <User className="w-4 h-4 mr-2" />
        {user.first_name}
        <ChevronDown className={`ml-2 h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
          >
            <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
              <DropdownLink to={`/${getUserRole(user.role)}/dashboard`}>Dashboard</DropdownLink>
              <button
                onClick={onLogout}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-900 transition-all duration-300"
              >
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [ref, inView] = useInView({
    threshold: 0,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const navItems = useMemo(
    () => [
      { path: '/', label: 'Home' },
      { path: '/about', label: 'About' },
      { path: '/services', label: 'Services' },
      { path: '/contact', label: 'Contact' },
    ],
    []
  );

  const loginItems = useMemo(
    () => [
      { path: '/login', label: 'Login' },
      { path: '/register', label: 'Register' },
    ],
    []
  );

  const isActive = useCallback((path) => location.pathname === path, [location]);

  return (
    <motion.nav
      ref={ref}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed w-full z-50 bg-indigo-900 shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                src="/7.png"
                alt="LoadLink Logo"
                className="h-8 w-auto mr-2"
              />
              <motion.span
                className="text-white text-xl font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                LoadLink
              </motion.span>
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <NavLink key={item.path} to={item.path} isActive={isActive(item.path)}>
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <UserMenu user={user} onLogout={handleLogout} />
            ) : (
              <LoginButton />
            )}
          </div>
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-indigo-800 focus:ring-white transition-all duration-300"
            >
              <span className="sr-only">Open main menu</span>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={isMobileMenuOpen ? 'close' : 'open'}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMobileMenuOpen ? (
                    <X className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-indigo-800">
              {navItems.map((item) => (
                <MobileNavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  isActive={isActive(item.path)}
                >
                  {item.label}
                </MobileNavLink>
              ))}
              {user ? (
                <>
                  <MobileNavLink
                    to={`/${getUserRole(user.role)}/dashboard`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    isActive={isActive(`/${getUserRole(user.role)}/dashboard`)}
                  >
                    Dashboard
                  </MobileNavLink>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-indigo-700 hover:text-white transition-all duration-300"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <MobileNavLink
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    isActive={isActive('/login')}
                  >
                    Login
                  </MobileNavLink>
                  <MobileNavLink
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    isActive={isActive('/register')}
                  >
                    Register
                  </MobileNavLink>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default React.memo(Navbar);
